import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowLeft, ArrowRight, MapPin } from "lucide-react";

// Light-theme image slider hero — bright architectural shots, fade transitions,
// auto-advance, arrow controls, numbered indicators.
const SLIDES = [
  {
    img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=90",
    label: "THE AZURE RESIDENCES",
    caption: "3 & 4 BHK Sky Residences · Patia",
  },
  {
    img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=2400&q=90",
    label: "KLIVING PLAZA",
    caption: "Grade-A Commercial Tower · Kalinga Nagar",
  },
  {
    img: "https://images.unsplash.com/photo-1613545325268-9265e1609167?auto=format&fit=crop&w=2400&q=90",
    label: "THE COVE VILLAS",
    caption: "Boutique Villas · Jatni",
  },
  {
    img: "https://images.unsplash.com/photo-1600566753051-6057e1c1c9e0?auto=format&fit=crop&w=2400&q=90",
    label: "THE MERIDIAN EXCHANGE",
    caption: "Mixed-use Landmark · Chandrasekharpur",
  },
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.5 + i * 0.12, duration: 1.0, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: 1.4 + i * 0.08, duration: 0.8 } }),
};

export default function HeroV3() {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  const next = useCallback(() => setIndex((i) => (i + 1) % SLIDES.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    timerRef.current = setInterval(next, 6000);
    return () => clearInterval(timerRef.current);
  }, [next]);

  // Reset autoplay timer on manual navigation
  const jumpTo = (i) => {
    setIndex(i);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(next, 6000);
  };

  const active = SLIDES[index];

  return (
    <section
      data-testid="hero-v3"
      className="relative min-h-[100svh] bg-editorial text-obsidian overflow-hidden"
    >
      {/* SLIDER — light imagery with soft cream tint */}
      <AnimatePresence mode="sync">
        <motion.div
          key={active.img}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
        >
          <img src={active.img} alt={active.label} className="w-full h-full object-cover" />
          {/* Light soft overlay — barely darken, preserve light feel */}
          <div className="absolute inset-0 bg-editorial/25" />
          {/* Cream gradient bottom-left for text readability */}
          <div className="absolute inset-0 bg-gradient-to-tr from-editorial/85 via-editorial/25 to-transparent" />
          {/* Soft top fade so nav reads */}
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-editorial/70 to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Top meta */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.8 }}
        className="absolute top-[130px] left-6 md:left-10 z-10 flex items-center gap-3 chapter-num text-obsidian/70"
      >
        <span className="w-8 h-px bg-emerald_kl" />
        <span>001 / KLIVING · MMXXV — DEMO 03</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.8 }}
        className="absolute top-[130px] right-6 md:right-10 z-10 flex items-center gap-2 chapter-num text-obsidian/70"
      >
        <MapPin className="w-3 h-3 text-emerald_kl" /> 20.29°N · 85.82°E — BHUBANESWAR
      </motion.div>

      {/* Headline block */}
      <div className="relative z-10 min-h-[100svh] flex flex-col justify-center px-6 md:px-10 max-w-[1400px] mx-auto">
        <div className="max-w-[1100px]">
          <motion.div
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="chapter-num text-emerald_kl mb-6 md:mb-8"
          >
            — LANDMARK DEVELOPMENTS · SINCE 2019
          </motion.div>

          <h1 className="font-serif-display text-obsidian leading-[0.92] tracking-[-0.02em] text-[52px] sm:text-[72px] md:text-[96px] lg:text-[128px]">
            <span className="mask-line"><motion.span custom={0} variants={lineVariants} initial="hidden" animate="show" className="italic font-light">Crafting</motion.span></span>
            <span className="mask-line"><motion.span custom={1} variants={lineVariants} initial="hidden" animate="show">Landmark</motion.span></span>
            <span className="mask-line"><motion.span custom={2} variants={lineVariants} initial="hidden" animate="show">
              Developments<span className="text-emerald_kl">.</span>
            </motion.span></span>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <motion.p
              variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="md:col-span-7 text-obsidian/75 text-base md:text-lg leading-relaxed max-w-lg"
            >
              A boutique developer building residential and commercial landmarks in Bhubaneswar — where materials are chosen slowly, drawings are drawn twice, and every plot is treated as public architecture.
            </motion.p>

            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="md:col-span-5 flex flex-col items-start md:items-end gap-3">
              {/* Dark solid button with LIGHT title text */}
              <a
                href="#projects"
                data-testid="hero-v3-cta-portfolio"
                className="btn-kl w-full sm:w-auto justify-center md:justify-start bg-obsidian text-editorial border-obsidian hover:bg-emerald_kl hover:border-emerald_kl"
              >
                View Portfolio <span className="btn-dot bg-editorial" />
              </a>
              <a
                href="#contact"
                data-testid="hero-v3-cta-visit"
                className="btn-kl w-full sm:w-auto justify-center md:justify-start bg-obsidian/0 text-obsidian border-obsidian/40 hover:bg-obsidian hover:text-editorial hover:border-obsidian"
              >
                Schedule a Visit <span className="btn-dot" />
              </a>
            </motion.div>
          </div>
        </div>

        {/* Slide caption (bottom-left) */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0, duration: 0.6 }}
          className="absolute bottom-8 left-6 md:left-10 flex items-center gap-5 text-obsidian/80"
        >
          <div className="flex items-center gap-2">
            <ArrowDown className="w-4 h-4 animate-bounce text-emerald_kl" />
            <span className="chapter-num">SCROLL</span>
          </div>
          <div className="hidden md:block h-6 w-px bg-obsidian/20" />
          <AnimatePresence mode="wait">
            <motion.div
              key={active.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
              className="hidden md:block"
            >
              <div className="chapter-num text-emerald_kl">NOW SHOWING</div>
              <div className="text-obsidian text-sm mt-1">
                <span className="font-medium">{active.label}</span>
                <span className="text-obsidian/50"> · {active.caption}</span>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Slide controls (bottom-right) */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0, duration: 0.6 }}
          className="absolute bottom-8 right-6 md:right-10 flex items-center gap-4"
        >
          <div className="hidden md:flex items-center gap-2 chapter-num text-obsidian/70">
            <span className="text-obsidian tabular-nums font-medium">{String(index + 1).padStart(2, "0")}</span>
            <span className="text-obsidian/40">/</span>
            <span>{String(SLIDES.length).padStart(2, "0")}</span>
          </div>

          {/* Progress dots */}
          <div className="flex items-center gap-2">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => jumpTo(i)}
                aria-label={`Slide ${i + 1}`}
                data-testid={`hero-v3-dot-${i}`}
                className={`h-1.5 transition-all duration-500 ${
                  i === index ? "w-10 bg-emerald_kl" : "w-4 bg-obsidian/20 hover:bg-obsidian/40"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2 ml-2">
            <button
              onClick={() => { prev(); clearInterval(timerRef.current); timerRef.current = setInterval(next, 6000); }}
              className="w-10 h-10 grid place-items-center border border-obsidian/30 text-obsidian hover:bg-obsidian hover:text-editorial transition-colors duration-300"
              aria-label="Previous slide"
              data-testid="hero-v3-prev"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => { next(); clearInterval(timerRef.current); timerRef.current = setInterval(next, 6000); }}
              className="w-10 h-10 grid place-items-center border border-obsidian/30 text-obsidian hover:bg-obsidian hover:text-editorial transition-colors duration-300"
              aria-label="Next slide"
              data-testid="hero-v3-next"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
